/**
 * The supplier the catalog grid shows for an item. Built by
 * `rollupPrimarySuppliers` from the item's `item_suppliers` rows.
 */
export interface ItemPrimarySupplier {
  /** Null when the item has several suppliers and none is flagged primary. */
  vendorId: string | null;
  vendorName: string | null;
  supplierPartNumber: string | null;
  /** False when the row is the item's only supplier but nobody flagged it primary. */
  isPrimary: boolean;
  /** Every approved supplier on the item, primary included. */
  supplierCount: number;
}

export interface SupplierRollupRow {
  item_id: string;
  vendor_id: string;
  vendor_name: string | null;
  supplier_part_number: string | null;
  is_primary: boolean;
}

/**
 * Pick one supplier per item: the row flagged `is_primary`, or the only
 * supplier when none is flagged — the same fallback purchasing
 * (`lib/uom.ts`) and the planner use. Several suppliers with no primary
 * yield a null vendor with `supplierCount` > 1 so the grid can say so
 * instead of guessing.
 */
export function rollupPrimarySuppliers(
  rows: Iterable<SupplierRollupRow>,
): Map<string, ItemPrimarySupplier> {
  const byItem = new Map<string, ItemPrimarySupplier>();
  for (const row of rows) {
    const candidate: ItemPrimarySupplier = {
      vendorId: row.vendor_id,
      vendorName: row.vendor_name,
      supplierPartNumber: row.supplier_part_number?.trim() || null,
      isPrimary: row.is_primary,
      supplierCount: 1,
    };
    const existing = byItem.get(row.item_id);
    if (!existing) {
      byItem.set(row.item_id, candidate);
      continue;
    }
    const supplierCount = existing.supplierCount + 1;
    if (existing.isPrimary) {
      existing.supplierCount = supplierCount;
    } else if (candidate.isPrimary) {
      byItem.set(row.item_id, { ...candidate, supplierCount });
    } else {
      byItem.set(row.item_id, {
        vendorId: null,
        vendorName: null,
        supplierPartNumber: null,
        isPrimary: false,
        supplierCount,
      });
    }
  }
  return byItem;
}
